import { useState } from 'react';
import { Button, Card, Pill } from '@salonomy/ui';
import { useResidencies } from '../../data/practitioner.js';
import { useAuth } from '../../lib/auth.js';
import { StartPracticeModal } from './StartPracticeModal.js';

/**
 * WS11 — the Settings "Your own practice" surface (brief 11 §1; ADR-0013). Lets an authenticated
 * practitioner open their OWN one-chair studio on the same account they already use — one login across
 * every salon they work in, plus their own studio in between (brief 11 non-negotiable #7). The actual
 * "start your practice" flow lives in StartPracticeModal; this panel is the honest signpost + list.
 *
 * GATING is about the HUMAN, not the current tenant's role: starting your own studio is a personal action,
 * so any signed-in practitioner can do it (reception accounts hold no chair anywhere, so the API simply
 * makes them the owner of the new tenant — the same as anyone else). We read the caller's OWN residencies
 * (the identity-spanning union — never the current salon's roster) to show studios they already own and to
 * avoid pushing "start a practice" at someone who already has one.
 *
 * Self-fetching so the integrator can mount it with no props, mirroring RecurringSeriesPanel / BillingPanel.
 */
export function PracticePanel() {
  const { user } = useAuth();
  const { data: residencies, isLoading } = useResidencies(Boolean(user));
  const [open, setOpen] = useState(false);

  // The caller's OWN private-practice legs (isPrivatePractice), active first. These are studios on THIS
  // account — distinct tenants the practitioner owns, surfaced from the identity union.
  const studios = (residencies ?? []).filter((r) => r.isPrivatePractice);
  const activeStudios = studios.filter((s) => s.active);
  const hasActiveStudio = activeStudios.length > 0;

  return (
    <Card>
      <div
        style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12 }}
      >
        <div>
          <div className="section-title">Your own practice</div>
          <div className="muted" style={{ fontSize: 13, marginTop: 4, lineHeight: 1.5 }}>
            Open your own one-chair studio on this account — its own diary, clients and booking
            page. One login for every salon you work in, and your own studio in between.
          </div>
        </div>
        <Button size="sm" onClick={() => setOpen(true)}>
          {hasActiveStudio ? 'Add another studio' : 'Start your own practice'}
        </Button>
      </div>

      {isLoading ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0' }}>
          Loading your account…
        </div>
      ) : studios.length === 0 ? (
        <div className="muted" style={{ fontSize: 13, padding: '12px 0 0', lineHeight: 1.5 }}>
          You don’t have a studio of your own yet. Your studio is billed on the Solo plan (a
          one-chair tenant, proposed at £15/month) — and if you rent a chair in a salon that’s on
          Salonomy, the salon pays for that chair, so you’re never billed twice for the same chair.
        </div>
      ) : (
        <div className="settings-list" style={{ marginTop: 8 }}>
          {studios.map((s) => (
            <div key={s.staffId} className="settings-row">
              <div>
                <div style={{ fontWeight: 600 }}>{s.salonName}</div>
                <div className="muted" style={{ fontSize: 12.5 }}>
                  Your one-chair studio · Solo plan
                </div>
              </div>
              <div style={{ display: 'flex', gap: 6, alignItems: 'center' }}>
                <Pill tone="cobalt" className="mono">
                  Studio
                </Pill>
                {s.active ? <Pill tone="success">Active</Pill> : <Pill tone="neutral">Closed</Pill>}
              </div>
            </div>
          ))}
        </div>
      )}

      <StartPracticeModal open={open} onClose={() => setOpen(false)} />
    </Card>
  );
}
